import {z} from '@hono/zod-openapi';

import {HONDA_FACILITY_PARAM_KEYS} from '../constants/HONDA_FACILITY_PARAM_KEYS';
import {hondaReportDateSchema} from './hondaReportDateSchema';
import {hondaReportTextSchema} from './hondaReportTextSchema';

/**
 * Order type codes from block <I> / field <t> of block <P>.
 * Source: "Popis struktury měsíčního hlášení dealerů Honda ČR a SR", v2.3 (1.12.2025).
 */
const HONDA_ORDER_TYPES = ['U', 'K', 'B', 'Z', 'L', 'D', 'I', 'R', 'S', 'W'] as const;
const hondaOrderTypeSchema = z.enum(HONDA_ORDER_TYPES);

const HONDA_EMPLOYEE_CATEGORIES = [
  'Workshop Manager',
  'Mechanic',
  'Other',
  'Receptionist',
  'Parts staff',
] as const;
const hondaEmployeeCategorySchema = z.enum(HONDA_EMPLOYEE_CATEGORIES);

/**
 * Fixed set of 8 (department, equipment) combinations for block <L>.
 * Real-world sample files use "Frame" (singular) even though the spec prose says "Frames" —
 * this follows the sample, since that's the value actually accepted by Honda/Caris.
 */

const gdprConsentSchema = z.union([z.literal(0), z.literal(1)]).optional();

const hondaVehicleCountsSchema = z.object({
  newActiveInWarranty: z.number().int().nonnegative(),
  activeInWarranty: z.number().int().nonnegative(),
  newActiveOutOfWarranty: z.number().int().nonnegative(),
  activeOutOfWarranty: z.number().int().nonnegative(),
  newlyInactive: z.number().int().nonnegative(),
  inactive: z.number().int().nonnegative(),
});

const hondaLabourSummarySchema = z.object({
  orderType: hondaOrderTypeSchema,
  orderCount: z.number().int().nonnegative(),
  totalBilledHours: z.number().finite(),
  ownLabourRevenue: z.number().finite(),
  externalLabourRevenue: z.number().finite(),
  hourlyRate: z.number().finite().nonnegative(),
});

const hondaMileageSchema = z.object({
  avgAnnualKmInWarranty: z.number().finite().nonnegative(),
  avgAnnualKmOutOfWarranty: z.number().finite().nonnegative(),
  avgAnnualKmAll: z.number().finite().nonnegative(),
});

const hondaPartsOverCounterSchema = z.object({
  unitsSold: z.number().int(),
  totalPriceExclVat: z.number().finite(),
});

const hondaPartsInOrdersSchema = z.object({
  originalPartsRevenue: z.number().finite(),
  nonOriginalPartsRevenue: z.number().finite(),
  bodyAndPaintPartsRevenue: z.number().finite(),
});

const hondaEmployeeCountSchema = z.object({
  category: hondaEmployeeCategorySchema,
  total: z.number().int().nonnegative(),
  paintShop: z.number().int().nonnegative(),
  bodyShop: z.number().int().nonnegative(),
  service: z.number().int().nonnegative(),
  partsWarehouse: z.number().int().nonnegative(),
  other: z.number().int().nonnegative(),
});

const hondaFacilityParamSchema = z.object({
  department: z.enum(['Paint Shop', 'Body Repair', 'Service', 'Stock']),
  equipment: hondaReportTextSchema,
  value: z.number().int().nonnegative(),
});
type HondaFacilityParam = z.infer<typeof hondaFacilityParamSchema>;

const HONDA_FACILITY_PARAM_KEY_SET = new Set(
  HONDA_FACILITY_PARAM_KEYS.map(([department, equipment]) => `${department}/${equipment}`)
);

/**
 * The generator falls back to 0 for any of the 8 fixed (department, equipment) combinations it
 * can't find a match for (see buildHondaMonthlyReportXml's facilityParamsNode) — that fallback
 * must never be reached with real input, so it's enforced here: reject anything other than
 * exactly those 8 combinations, each exactly once (no unknown pair, no duplicate silently
 * shadowing a missing one).
 */
function isExactlyTheEightHondaFacilityParamCombinations(params: HondaFacilityParam[]): boolean {
  const keys = params.map((param) => `${param.department}/${param.equipment}`);

  return (
    keys.length === new Set(keys).size && keys.every((key) => HONDA_FACILITY_PARAM_KEY_SET.has(key))
  );
}

/**
 * Block <P> — one closed service order. Carries full customer + vehicle-user PII
 * (GDPR-sensitive, see `<n>`/gdprConsent). Field lengths match the spec's max-length notes.
 */
const hondaRepairHistoryEntrySchema = z.object({
  internalOrderNumber: z.string().trim().min(1).max(30).pipe(hondaReportTextSchema),
  vin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/),
  warrantyStartDate: z.union([hondaReportDateSchema, z.literal('')]).default(''),
  orderType: hondaOrderTypeSchema,
  faultDescription: z.string().max(1000).pipe(hondaReportTextSchema).optional(),
  deliveryDate: hondaReportDateSchema,
  dealerCode: z
    .string()
    .regex(/^\d{3}$/)
    .pipe(hondaReportTextSchema),
  odometer: z.number().int().nonnegative(),
  customerInternalId: z.string().trim().min(1).max(15).pipe(hondaReportTextSchema),
  customerFirstName: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerLastName: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerCompany: z.string().max(80).pipe(hondaReportTextSchema).optional(),
  customerIco: z.string().max(25).pipe(hondaReportTextSchema).optional(),
  customerDic: z.string().max(25).pipe(hondaReportTextSchema).optional(),
  customerStreet: z.string().max(60).pipe(hondaReportTextSchema).optional(),
  customerCity: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerZip: z.string().max(10).pipe(hondaReportTextSchema).optional(),
  customerPhone: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerFax: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerHomePhone: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  customerNote: z.string().max(1000).pipe(hondaReportTextSchema).optional(),
  customerMobile: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  gdprConsent: gdprConsentSchema,
  customerEmail: z.string().max(60).pipe(hondaReportTextSchema).optional(),
  vehicleUserFirstName: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  vehicleUserLastName: z.string().max(50).pipe(hondaReportTextSchema).optional(),
  vehicleUserPhone: z.string().max(30).pipe(hondaReportTextSchema).optional(),
  vehicleUserEmail: z.string().max(60).pipe(hondaReportTextSchema).optional(),
});

export const hondaMonthlyReportRequestSchema = z
  .object({
    /** DMS version string, free-form (e.g. Omnetic release tag). */
    dmsVersion: z.string().trim().min(1).pipe(hondaReportTextSchema),
    dealerCode: z
      .string()
      .regex(/^\d{3}$/)
      .pipe(hondaReportTextSchema),
    reportMonth: z.number().int().min(1).max(12),
    reportYear: z.number().int().min(1000).max(9999),
    generatedAt: z.string().datetime({offset: true}).openapi({example: '2026-09-05T12:55:35Z'}),
    vehicleCounts: hondaVehicleCountsSchema,
    labourSummary: z
      .array(hondaLabourSummarySchema)
      .max(10)
      .refine(
        (entries) => new Set(entries.map((entry) => entry.orderType)).size === entries.length,
        {message: 'Each Honda order type must appear at most once in labourSummary'}
      ),
    mileage: hondaMileageSchema,
    partsOverCounter: hondaPartsOverCounterSchema,
    partsInOrders: hondaPartsInOrdersSchema,
    employeeCounts: z
      .array(hondaEmployeeCountSchema)
      .max(6)
      .refine(
        (entries) => new Set(entries.map((entry) => entry.category)).size === entries.length,
        {message: 'Each Honda employee category must appear at most once in employeeCounts'}
      ),
    facilityParams: z
      .array(hondaFacilityParamSchema)
      .length(8)
      .refine(isExactlyTheEightHondaFacilityParamCombinations, {
        message:
          "facilityParams must contain exactly Honda's spec's 8 (department, equipment) " +
          `combinations, each once: ${HONDA_FACILITY_PARAM_KEYS.map(([d, e]) => `${d}/${e}`).join(', ')}`,
      }),
    repairHistory: z.array(hondaRepairHistoryEntrySchema),
  })
  .openapi('HondaMonthlyReportRequest');
